import React, { useEffect, useRef } from 'react';
import { ContextFlow } from './ContextFlow';

// The section opens on Mariana's line, above the Why Nisa eyebrow. The page
// used to go from product category straight into operational pain with no
// moment saying why any of it matters. Stakes first, then the H2 delivers the
// tension.
//
// Real quote, said at the in-person team retreat. Do not paraphrase it, do not
// extend it, do not strip the attribution. Seven words is the whole point.
export const OperationalTension = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const scrollBlobRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const section = sectionRef.current;
    const blob = scrollBlobRef.current;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!section || !blob || reducedMotion.matches) {
      return;
    }
    let animationFrame = 0;
    const updateBlob = () => {
      animationFrame = 0;
      const rect = section.getBoundingClientRect();
      const progress = Math.min(1, Math.max(0, (window.innerHeight - rect.top) / (window.innerHeight + rect.height)));
      blob.style.setProperty('--brand-scroll-rotation', `${progress * 120}deg`);
      blob.style.setProperty('--brand-scroll-hue', `${progress * 105}deg`);
      blob.style.setProperty('--brand-scroll-shift', `${(progress - 0.5) * 18}px`);
      blob.style.setProperty('--brand-scroll-scale', `${1 + progress * 0.08}`);
    };
    const requestUpdate = () => {
      if (!animationFrame) {
        animationFrame = window.requestAnimationFrame(updateBlob);
      }
    };
    updateBlob();
    window.addEventListener('scroll', requestUpdate, {
      passive: true
    });
    window.addEventListener('resize', requestUpdate);
    return () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, []);
  return <section ref={sectionRef} id="the-problem" className="w-full border-t border-gray-100 bg-white" data-id="element-208" data-mp="2ojsou6x8j76i">
      <div className="container mx-auto px-5 py-section-mobile sm:px-8 md:py-section-desktop" style={{
      paddingTop: "40px",
      paddingBottom: "40px"
    }} data-id="element-209" data-mp="2jkxty6lzts61">
        <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-210" data-mp="2tjx5py2223x9">
          <div className="lg:col-span-7" data-id="element-211" data-mp="2ti2fq5bxflmb">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-primary-600" data-id="element-212" data-mp="25vdxtjmx3t5p">Why Nisa</p>
            <h2 className="text-balance font-serif text-h2-mobile font-normal leading-tight text-teal-950 md:text-h2-desktop" data-id="element-213" data-mp="20wkgfg4ifi09">
              Coaching teams are stretched across too many teachers, tasks, and tools
            </h2>
            <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-gray-600 md:text-lg" data-id="element-214" data-mp="2arch4ste2kz9">
              High teacher-to-coach ratios, administrative demands, and scattered context leave little time for the work that matters most. Coaching can be lonely, too. Few coaches have a thought partner who knows their teachers, goals, and curriculum well enough to help in the moment.
            </p>
            <p className="mt-5 max-w-2xl text-[17px] leading-relaxed text-gray-600 md:text-lg" data-id="element-215" data-mp="2fipsjynr4je">
              So every conversation opens by rebuilding what the last one already established.
            </p>
          </div>

          <div className="lg:col-span-5" data-id="element-216" data-mp="gd775htintjp">
            <div className="group relative mx-auto max-w-md py-4" data-id="element-217" data-mp="geidnpecjfei">
              <div ref={scrollBlobRef} aria-hidden="true" className="brand-scroll-blob pointer-events-none absolute -inset-4 rounded-[42%_58%_62%_38%/48%_38%_62%_52%] opacity-[0.69]" data-id="element-218" data-mp="lctnw18jzrx9" />
              <div aria-hidden="true" className="brand-breathing-glow pointer-events-none absolute inset-x-4 bottom-2 top-12 rounded-[46%_54%_58%_42%/56%_44%_56%_44%] opacity-55 transition-opacity duration-700 group-hover:opacity-80" data-id="element-219" data-mp="be46e9kkk9hj" />
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2Huxx8rff8YtszHy2fW7f1/Nisa_Duo.png" alt="Illustration of a coach and teacher in conversation" className="brand-illustration relative w-full object-contain" loading="lazy" data-id="element-220" data-mp="2w5e9xvkwok06" />
            </div>
          </div>
        </div>
      </div>

      <ContextFlow data-id="element-221" data-mp="32mpruut3h5mk" />
    </section>;
};